import { EntityType, Locale, convertLanguage } from '@frontend/common';
import { AsyncComponent, ObjectBadgeMapper, StateBadge, isUUID } from '@frontend/components/common';
import { AddressBadge, BooleanBadge, DateBadge, DimensionBadge, LocationBadge, RelativeDateBadge } from '@frontend/elements';
import { CommonMessage } from '@frontend/lang';
import { Logger } from '@frontend/logger';
import { isValid, parseISO } from 'date-fns';
import React, { ReactElement } from 'react';
import { LuAsterisk } from 'react-icons/lu';
import { FormattedMessage } from 'react-intl';

// Helper function to validate ISO date strings
function isValidISODate(dateString: string): boolean {
    try {
        const date = parseISO(dateString);
        return isValid(date);
    } catch {
        return false;
    }
}

interface Props<T extends object> {
    object: T;
    /**
     * @description Can be used for keys in the object mapper that dont exist on the object itself.
     */
    objectBadgeKeyOverwrite?: Map<keyof T, string>;
    /**
     * @description Can be used to let the component know which key to use for the object badge.
     */
    objectBadgeValueKeyOverwrite?: Map<keyof T, string>;
    keyOverwrite?: Map<keyof T | string, React.ReactNode | ((obj: T) => React.ReactNode)>;
    /**
     * @description will overwrite the value of the key provided.
     */
    valueOverwrite?: Map<keyof T | string, (obj: T) => React.ReactNode>;
    /**
     * @description will hide the keys provided. If not provided, all keys will be shown.
     */
    hidden?: (keyof T)[];
    /**
     * @description will only show the keys provided in the same order as provided. If not provided, all keys will be shown.
     * Keys provided in the hidden array will overrule key provided here.
     */
    order?: (keyof T | string)[];
    /**
     * @deprecated
     * should no longer be handled seperately
     */
    rowClass?: string;
    type?: 'column' | 'table';
    cols?: number;
    queryParamNavigation?: boolean;
}

const styles = {
    column: {
        1: 'grid-cols-1',
        2: 'grid-cols-2',
        3: 'grid-cols-3',
        4: 'grid-cols-4',
        5: 'grid-cols-5'
    }
};

export const ObjectDetailMapper = <T extends object>(props: Props<T>) => {
    const rows = convert(props);
    if (props.type === 'column') {
        return (
            <div className={`grid w-full ${props.cols ? styles.column[props.cols as keyof typeof styles.column] : 'grid-cols-3'} gap-4`}>
                {rows.map(([key, keyDisplay, value]) => {
                    return (
                        <div
                            key={key}
                            data-testid={key}
                            className='flex min-h-12 min-w-0 flex-col items-start justify-start gap-0.75 self-stretch overflow-hidden'>
                            <div className='text-brand-muted text-base font-medium'>{keyDisplay}</div>
                            <div className='text-foreground-default max-w-full min-w-0 justify-start text-base font-medium break-all'>{value}</div>
                        </div>
                    );
                })}
            </div>
        );
    }
    return (
        <>
            {rows.length > 0 && (
                <table data-testid='object-detail-table'>
                    {rows.map(([key, keyDisplay, value]) => (
                        <tr
                            data-testid={key}
                            key={key}>
                            <td className='text-brand-muted me-2 py-1 pe-4 text-base font-medium'>{keyDisplay}</td>
                            <td>
                                <div className='text-foreground-default ms-2 mb-0 w-full justify-start overflow-hidden text-base font-medium'>{value}</div>
                            </td>
                        </tr>
                    ))}
                </table>
            )}
        </>
    );
};

export function convert<T extends object>(props: Props<T>): [string, React.ReactNode, React.ReactNode][] {
    const result: [string, React.ReactNode, React.ReactNode][] = [];
    const keysToRender: (keyof T | string)[] = props.order ? props.order : Object.keys(props.object);

    for (const key of keysToRender) {
        let value = (props.object as any)[key];

        if (props.hidden && props.hidden.includes(key as keyof T)) {
            continue;
        }
        let finalKey = null;
        const translatedKey = props.keyOverwrite?.get(key as keyof T);
        if (translatedKey) {
            // Check if translatedKey is a function, if so call it with the object
            finalKey = typeof translatedKey === 'function' ? translatedKey(props.object) : translatedKey;
        } else if (Object.keys(translationKeys).includes((key as string).slice(-5))) {
            const found = Object.entries(translationKeys).find((k) => (key as string).includes(k[0]));
            finalKey = (key as string)[0].toUpperCase() + (key as string).slice(1).replace(found![0], `(${found![1]})`);
            finalKey = finalKey.replace(/_/g, ' ');
        } else if (findTranslationByKey(key as string)) {
            finalKey = findTranslationByKey(key as string);
        } else {
            const splitKey = (key as string)
                .split('_')
                .filter((k) => k !== 'id')
                .join(' ');
            if (splitKey) {
                finalKey = splitKey[0].toUpperCase() + splitKey.slice(1);
                finalKey = finalKey.replace(/_/g, ' ').replace(/timestamp/g, '');
            }
        }

        let finalValue: React.ReactNode = <></>;
        if (props.valueOverwrite && props.valueOverwrite.has(key as keyof T)) {
            const overwriteFn = props.valueOverwrite.get(key as keyof T);
            if (overwriteFn) {
                finalValue = overwriteFn(props.object as T);
            }
        } else if (value === null || value === undefined || (typeof value === 'string' && value.length === 0)) {
            finalValue = <span>/</span>;
        } else if (typeof value === 'string') {
            if (isUUID(value)) {
                if (key === 'id') {
                    finalValue = <span>{value}</span>;
                } else if (key === 'entity_id' && Object.keys(props.object).includes('entity_type')) {
                    finalValue = (
                        <AsyncComponent>
                            <ObjectBadgeMapper
                                id={value}
                                type={(props.object as { entity_type: EntityType }).entity_type + '_id'}
                                queryParamNavigation={props.queryParamNavigation}
                            />
                        </AsyncComponent>
                    );
                } else {
                    finalValue = (
                        <ObjectBadgeMapper
                            id={value}
                            type={
                                props.objectBadgeKeyOverwrite && props.objectBadgeKeyOverwrite.has(key as keyof T)
                                    ? props.objectBadgeKeyOverwrite.get(key as keyof T)!
                                    : (key as string)
                            }
                            queryParamNavigation
                        />
                    );
                }
            } else if (key === 'heartbeat_timestamp') {
                if (isValidISODate(value)) {
                    finalValue = (
                        <RelativeDateBadge
                            date={value}
                            displayTime
                            inline
                        />
                    );
                } else {
                    finalValue = <span>{value}</span>;
                }
            } else if ((key as string).includes('time') || (key as string).includes('date')) {
                if (isValidISODate(value)) {
                    finalValue = (
                        <DateBadge
                            date={value}
                            displayTime={(key as string).includes('time')}
                            inline
                        />
                    );
                } else {
                    finalValue = <span>{value}</span>;
                }
            } else if ((key as string).includes('address')) {
                const city = 'city' in props.object ? props.object['city'] : undefined;
                const zip = 'zip_code' in props.object ? props.object['zip_code'] : undefined;
                finalValue = (
                    <AddressBadge
                        address={value}
                        city={city as string | undefined}
                        zip={zip as string | undefined}
                    />
                );
            } else if (key === 'state') {
                const state = 'state' in props.object ? props.object['state'] : undefined;
                finalValue = <StateBadge state={state as any} />;
            } else if (key === 'language') {
                finalValue = convertLanguage(value as Locale);
            } else if (key === 'secret') {
                finalValue = <span>{Array(8).fill(<LuAsterisk size={10} />)}</span>;
            } else {
                finalValue = <span>{value}</span>;
            }
        } else if (typeof value === 'number') {
            finalValue = <div className='bg-border-default inline-flex items-center justify-center rounded px-1'>{value}</div>;
        } else if (typeof value === 'boolean') {
            finalValue = <BooleanBadge value={value} />;
        } else if (value === null || value === undefined) {
            finalValue = <span>-</span>;
        } else if (key === 'location' || key === 'absolute') {
            finalValue = <LocationBadge location={value} />;
        } else if (key === 'dimension' || key === 'inner' || key === 'outer') {
            finalValue = <DimensionBadge dimension={value} />;
        } else if (Array.isArray(value)) {
            if (value.every((v) => typeof v === 'string' || typeof v === 'number')) {
                finalValue = (
                    <div className='row'>
                        {value.map((v) => (
                            <div
                                key={v}
                                className='col-xl-3 col-lg-4 col-md-6 col-12'
                                style={{ minWidth: 'fit-content' }}>
                                <span>{(v as string).replace(/[_.]/g, ' ')}</span>
                            </div>
                        ))}
                    </div>
                );
            } else if (value.every((v) => typeof v === 'object')) {
                finalValue = (
                    <div className='flex flex-wrap gap-4'>
                        {value.map((v, index) => (
                            <ObjectBadgeMapper
                                key={index}
                                id={v.entity_id}
                                type={v.entity_type + '_id'}
                                queryParamNavigation={props.queryParamNavigation}
                            />
                        ))}
                    </div>
                );
            }
        } else if (key === 'data') {
            finalValue = (
                <div className='flex flex-col'>
                    {Object.entries(value).map(([k, v]) => (
                        <div
                            key={k}
                            className='flex flex-row gap-2'>
                            <strong>{k}:</strong>
                            {typeof v === 'boolean' ? <BooleanBadge value={v} /> : (v as string | number)}
                        </div>
                    ))}
                </div>
            );
        } else if (typeof value === 'object') {
            finalValue = (
                <ObjectBadgeMapper
                    id={
                        props.objectBadgeValueKeyOverwrite?.get(key as keyof T)
                            ? (value as any)[props.objectBadgeValueKeyOverwrite.get(key as keyof T)!]
                            : (value as any).entity_id
                    }
                    type={props.objectBadgeKeyOverwrite?.get(key as keyof T) ?? value.entity_type + '_id'}
                    queryParamNavigation={props.queryParamNavigation}
                />
            );
        } else {
            Logger.warn('Unknown type', value);
        }

        result.push([key as string, finalKey as any, finalValue]);
    }

    return result;
}

const translationKeys = { nl_be: 'NL', en_us: 'EN', fr_fr: 'FR', de_de: 'DE' };

type TranslationValue = ReactElement<typeof FormattedMessage> | { [key: string]: TranslationValue } | ((...args: any[]) => TranslationValue);

function processFoundValue(value: TranslationValue): TranslationValue {
    if (typeof value === 'function') {
        return processFoundValue(value());
    }
    if (typeof value === 'object' && value !== null && !('$$typeof' in value)) {
        const firstKey = Object.keys(value)[0];
        if (firstKey) {
            const nestedValue = (value as { [key: string]: TranslationValue })[firstKey];
            return processFoundValue(nestedValue);
        }
    }

    return value;
}

function searchRecursively(node: TranslationValue, keyToFind: string): TranslationValue | null {
    if (typeof node !== 'object' || node === null || '$$typeof' in node) {
        return null;
    }

    const upperKeyToFind = keyToFind.toUpperCase();
    if (Object.prototype.hasOwnProperty.call(node, upperKeyToFind)) {
        const foundValue = (node as { [key: string]: TranslationValue })[upperKeyToFind];
        return processFoundValue(foundValue);
    }

    for (const key in node) {
        if (Object.prototype.hasOwnProperty.call(node, key)) {
            const value = (node as { [key: string]: TranslationValue })[key];

            const result = searchRecursively(value, upperKeyToFind);

            if (result !== null) {
                return result;
            }
        }
    }
    return null;
}

function findTranslationByKey(keyToFind: string): TranslationValue | null {
    const commonFieldsResult = searchRecursively(CommonMessage.OBJECTS?.COMMON_FIELDS as TranslationValue, keyToFind);
    if (commonFieldsResult !== null) {
        return commonFieldsResult;
    }
    return searchRecursively(CommonMessage as TranslationValue, keyToFind);
}
